import React, { useCallback, useRef } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Feather, Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';
import { RemoteImage } from '../shared/RemoteImage';
import type { MenuAnchor } from './OverflowMenu';

// Short hero on purpose: the ingredients have to start above the fold.
/**
 * Above WIDE_BREAKPOINT the hero is sized from the screen width at the same
 * 2.4:1 the phone renders, capped so it cannot eat the fold. The old flat 196
 * was a near-no-op next to the narrow 190, which is why an 820px iPad drew a
 * 4.2:1 letterbox (#632). The cap binds from 816px up.
 */
const HERO_MAX_HEIGHT = 340;
const HERO_RATIO = 2.4;
const HERO_HEIGHT_NARROW = 190;

const WIDE_BREAKPOINT = 768;

interface RecipeHeroImageProps {
  imageUrl: string;
  onBack: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  /** Narrow layouts only — on wide screens these live in the actions bar. */
  onShare?: () => void;
  onMore?: (anchor: MenuAnchor) => void;
}

export const RecipeHeroImage: React.FC<RecipeHeroImageProps> = ({
  imageUrl,
  onBack,
  isFavorite,
  onToggleFavorite,
  onShare,
  onMore,
}) => {
  const { t } = useTranslation();
  const { width: screenWidth } = useWindowDimensions();
  const isWide = screenWidth >= WIDE_BREAKPOINT;
  const heroHeight = isWide ? Math.min(HERO_MAX_HEIGHT, screenWidth / HERO_RATIO) : HERO_HEIGHT_NARROW;
  const size = isWide ? 40 : 36;
  const top = isWide ? 18 : 16;
  const edge = isWide ? 20 : 14;
  const iconSize = isWide ? 19 : 17;

  const moreRef = useRef<View>(null);

  // The ⋯ menu anchors to the button, so measure it in window space on press.
  const handleMore = useCallback(() => {
    if (!onMore) return;
    const node = moreRef.current;
    if (node && typeof node.measureInWindow === 'function') {
      node.measureInWindow((x, y, w, h) => {
        onMore({ top: y + h + 6, right: Math.max(screenWidth - (x + w), 8) });
      });
    } else {
      onMore({ top: top + size + 6, right: edge });
    }
  }, [onMore, screenWidth, top, size, edge]);

  // Right-hand cluster, outermost first: ⋯, share, heart.
  const showShare = !isWide && !!onShare;
  const showMore = !isWide && !!onMore;
  let rightOffset = edge;
  const morePos = rightOffset;
  if (showMore) rightOffset += size + 8;
  const sharePos = rightOffset;
  if (showShare) rightOffset += size + 8;
  const heartPos = rightOffset;

  const buttonStyle = { width: size, height: size, borderRadius: size / 2 };

  return (
    <View style={[styles.container, { width: screenWidth, height: heroHeight }]} testID="recipe-hero">
      <RemoteImage
        uri={imageUrl}
        style={styles.image}
        resizeMode="cover"
        fallback={
          <View style={[styles.image, styles.imageFallback]}>
            <Feather name="image" size={32} color={Paprika.textMuted} />
          </View>
        }
      />

      <LinearGradient
        colors={['transparent', `${Paprika.background}60`]}
        style={[styles.gradient, { height: heroHeight * 0.4 }]}
      />

      {/* Back — top left */}
      <TouchableOpacity
        style={[styles.floatingButton, buttonStyle, { top, left: edge }]}
        onPress={onBack}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityLabel={t('common.back', 'Retour')}
      >
        <Feather name="arrow-left" size={iconSize} color={Paprika.textPrimary} />
      </TouchableOpacity>

      {/* Overflow — outermost right, narrow layouts only */}
      {showMore && (
        <View ref={moreRef} style={[styles.anchor, { top, right: morePos }]} collapsable={false}>
          <TouchableOpacity
            style={[styles.floatingButton, styles.floatingButtonStatic, buttonStyle]}
            onPress={handleMore}
            testID="recipe-more"
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityLabel={t('recipeDetail.more', 'Plus')}
          >
            <Ionicons name="ellipsis-horizontal" size={iconSize} color={Paprika.textPrimary} />
          </TouchableOpacity>
        </View>
      )}

      {/* Share — narrow layouts only */}
      {showShare && (
        <TouchableOpacity
          style={[styles.floatingButton, buttonStyle, { top, right: sharePos }]}
          onPress={onShare}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel={t('recipeDetail.share', 'Partager')}
        >
          <Ionicons name="share-outline" size={iconSize} color={Paprika.textPrimary} />
        </TouchableOpacity>
      )}

      {/* Favorite — stays on the hero: frequent, reversible, icon-only. */}
      {onToggleFavorite && (
        <TouchableOpacity
          style={[
            styles.floatingButton,
            buttonStyle,
            { top, right: heartPos },
            isFavorite && styles.favoriteButtonActive,
          ]}
          onPress={onToggleFavorite}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel={t('recipeDetail.favorite', 'Favori')}
        >
          <Ionicons
            name={isFavorite ? 'heart' : 'heart-outline'}
            size={iconSize}
            color={isFavorite ? Paprika.accent : Paprika.textPrimary}
          />
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  imageFallback: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.surfaceHighest,
  },
  gradient: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
  },
  anchor: {
    position: 'absolute',
    zIndex: 5,
  },
  floatingButton: {
    position: 'absolute',
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#52443c',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 2,
    zIndex: 5,
  },
  /** Inside the measured anchor wrapper the button is laid out normally. */
  floatingButtonStatic: {
    position: 'relative',
  },
  favoriteButtonActive: {
    backgroundColor: 'rgba(134, 78, 44, 0.12)',
  },
});

export default RecipeHeroImage;
